import React, { type PropsWithChildren, useCallback } from 'react';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { type SafeAreaViewProps } from 'react-native-safe-area-context';

import {
  BodyText,
  Button,
  Modal,
  Navbar,
  ProductCardV2,
  theme,
  TransparentModal,
  useNavbarContainerHeight,
  useResponsive,
  useIsFocused,
} from '@sg/garnish';

import { useTrackEffect } from '@order/Telemetry';

import {
  FloatingBar,
  FloatingControls,
  LoadingAnimation,
  NotFoundView,
} from '../../../../components';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { useLastValidOrder } from '../../../../context/Order';
import {
  useIsFoldableDevice,
  useIsLiquidGlassEnabled,
  useWindowWidthAtLeast,
} from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import { useOrderProductConfirmProductScreenData } from './hooks/useOrderProductConfirmProductScreenData';

export const OrderProductConfirmProductScreen = () => {
  const { formatMessage } = useIntl();

  const { productSlug } = useLocalSearchParams<{ productSlug?: string }>();

  const isFocused = useIsFocused();

  const { baseProduct, isFetchingBaseProduct } = useOrderProductConfirmProductScreenData({
    productSlug: productSlug ?? '',
  });

  if (!isFocused) return null;

  if (isFetchingBaseProduct) {
    return (
      <>
        <OrderProductConfirmProductContainer>
          <Modal.Row style={styles.notLoadedViewContainer}>
            <LoadingAnimation size="large" />
          </Modal.Row>
        </OrderProductConfirmProductContainer>

        <OrderProductConfirmProductFloatingControls />
      </>
    );
  }

  if (!baseProduct?.isDeepLinkingEnabled) {
    return (
      <>
        <OrderProductConfirmProductContainer>
          <Modal.Row style={styles.notLoadedViewContainer}>
            <NotFoundView
              withoutFooter
              description={
                baseProduct && !baseProduct.isDeepLinkingEnabled
                  ? formatMessage(messages.orderProductNotAvailable)
                  : undefined
              }
            />
          </Modal.Row>
        </OrderProductConfirmProductContainer>

        <OrderProductConfirmProductFloatingControls />
      </>
    );
  }

  return <OrderProductConfirmProductLoaded baseProduct={baseProduct} />;
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const OrderProductConfirmProductContainer = (props: Pick<ViewProps, 'children'>) => {
  const navigateBack = useNavigateBack();
  const { match, minWidth } = useResponsive();
  const { formatMessage } = useIntl();
  const isFoldableDevice = useIsFoldableDevice();

  // ─── Derived ─────────────────────────────────────────────────────────

  const shouldApplyTopSafeArea = Platform.OS === 'android' || isFoldableDevice;
  const mobileSafeAreaEdges = shouldApplyTopSafeArea
    ? MOBILE_SAFE_AREA_EDGES_WITH_TOP
    : MOBILE_SAFE_AREA_EDGES;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const dismissModal = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      accessibilityLabel={formatMessage(messages.confirmProductA11yLabel)}
      fitHeight
      size={match(['full', 'medium'])}
      onClose={dismissModal}
      mobileEdges={mobileSafeAreaEdges}
    >
      {minWidth.isSM ? <Modal.FloatingCloseBtn onPress={dismissModal} /> : null}

      {props.children}
    </TransparentModal>
  );
};

const OrderProductConfirmProductScrollContainer = (props: PropsWithChildren) => {
  const { children } = props;

  const { currentBreakpoint } = useResponsive();
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentContainerStyles = currentBreakpoint.isXS
    ? [styles.scrollContentContainerXS, navbarPaddingBottomStyle]
    : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return <ScrollView contentContainerStyle={contentContainerStyles}>{children}</ScrollView>;
};

const OrderProductConfirmProductFloatingControls = (props: PropsWithChildren) => {
  const { children } = props;

  const navigateBack = useNavigateBack();
  const { currentBreakpoint } = useResponsive();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─────────────────────────────────────────────────────────────────────

  if (!currentBreakpoint.isXS) {
    return null;
  }

  return (
    <FloatingBar>
      <FloatingControls
        isLiquidGlassEnabled={isLiquidGlassEnabled}
        onBackButtonPress={navigateBack}
      >
        {children}
      </FloatingControls>
    </FloatingBar>
  );
};

const OrderProductConfirmProductLoaded = (props: OrderProductConfirmProductLoadedProps) => {
  const { baseProduct } = props;

  const { productSlug } = useLocalSearchParams<{ productSlug?: string }>();

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { currentBreakpoint, match } = useResponsive();
  const { fetchLastValidOrder, isFetchingOrders } = useLastValidOrder();
  const lastInteractedStore = useLastInteractedStore();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();
  const hasRoomForCenteredText = useWindowWidthAtLeast({ minWidth: CENTERED_TEXT_MIN_WINDOW_WIDTH }); // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleContinuePress = useCallback(() => {
    const path = lastInteractedStore
      ? '/order-product/[productSlug]/confirm-location'
      : '/order-product/[productSlug]/search-location';

    router.navigate({
      pathname: path,
      params: { productSlug: productSlug ?? '' },
    });
  }, [router, productSlug, lastInteractedStore]);

  const productCardContainerStyles = [
    styles.productCardContainer,
    match([styles.productCardContainerXS, styles.productCardContainerSM]),
  ];
  const centeredTextStyle = hasRoomForCenteredText ? styles.centeredText : undefined;
  const headlineStyles = [match([undefined, styles.headerSM]), centeredTextStyle];

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(fetchLastValidOrder); // fetch the last placed order on mount

  useTrackEffect('deep-linking.start');

  return (
    <>
      <OrderProductConfirmProductContainer>
        <OrderProductConfirmProductScrollContainer>
          {baseProduct.heading ? (
            <Modal.Headline sizeMatch={['32']} style={headlineStyles}>
              {baseProduct.heading}
            </Modal.Headline>
          ) : null}

          <Modal.Row style={styles.contentContainer}>
            {baseProduct.subheading ? (
              <BodyText sizeMatch={['14', '16']} style={centeredTextStyle}>
                {baseProduct.subheading}
              </BodyText>
            ) : null}

            <View style={productCardContainerStyles}>
              <ProductCardV2.Container canHover={false}>
                {baseProduct.asset?.url ? (
                  <ProductCardV2.Image source={baseProduct.asset.url} alt={baseProduct.name} />
                ) : null}

                <ProductCardV2.LabelContainer>
                  {baseProduct.isSeasonal ? (
                    <ProductCardV2.Label>
                      {formatMessage(messages.confirmProductSeasonal)}
                    </ProductCardV2.Label>
                  ) : null}

                  {baseProduct.name ? (
                    <ProductCardV2.Heading>{baseProduct.name}</ProductCardV2.Heading>
                  ) : null}

                  {baseProduct.description ? (
                    <ProductCardV2.Description>{baseProduct.description}</ProductCardV2.Description>
                  ) : null}
                </ProductCardV2.LabelContainer>
              </ProductCardV2.Container>
            </View>
          </Modal.Row>
        </OrderProductConfirmProductScrollContainer>

        {currentBreakpoint.isXS ? null : (
          <Modal.Footer style={styles.footerSM} withoutTopBorder>
            <Button
              size="large"
              disabled={isFetchingOrders}
              onPress={handleContinuePress}
              testID="order-product.confirm-product-continue"
              accessibilityLabel={formatMessage(messages.orderProductContinue)}
              accessibilityHint={formatMessage(messages.orderProductContinue)}
            >
              {formatMessage(messages.orderProductContinue)}
            </Button>
          </Modal.Footer>
        )}
      </OrderProductConfirmProductContainer>

      <OrderProductConfirmProductFloatingControls>
        <Navbar.Button
          isLiquidGlassEnabled={isLiquidGlassEnabled}
          title={formatMessage(messages.orderProductContinue)}
          disabled={isFetchingOrders}
          style={styles.navbarButton}
          onPress={handleContinuePress}
        />
      </OrderProductConfirmProductFloatingControls>
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CENTERED_TEXT_MIN_WINDOW_WIDTH = 500;

const MOBILE_SAFE_AREA_EDGES: SafeAreaViewProps['edges'] = [];
const MOBILE_SAFE_AREA_EDGES_WITH_TOP: SafeAreaViewProps['edges'] = ['top'];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderProductNotAvailable: {
    defaultMessage: 'That item is no longer available.',
    description: 'Order Product | Not Available',
  },
  confirmProductSeasonal: {
    defaultMessage: 'Seasonal',
    description: 'Confirm Product | Seasonal Label',
  },
  orderProductContinue: {
    defaultMessage: 'Order now',
    description: 'Order Product | Continue',
  },
  confirmProductA11yLabel: {
    defaultMessage: 'Confirm product',
    description: 'Order Product | Confirm product | Dialog a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  headerSM: {
    marginTop: theme.spacing['2'],
    paddingTop: theme.spacing['10'],
  },
  contentContainer: {
    flexGrow: 1,
  },
  centeredText: {
    textAlign: 'center',
  },
  notLoadedViewContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: theme.spacing['6'],
  },
  productCardContainer: {
    paddingTop: theme.spacing['6'],
    width: '100%',
    alignSelf: 'center',
  },
  productCardContainerXS: {
    maxWidth: 400,
  },
  productCardContainerSM: {
    maxWidth: 342,
  },
  scrollContentContainerXS: {
    paddingTop: theme.spacing['6'],
  },
  navbarButton: {
    width: 'auto',
    maxWidth: '100%',
    paddingHorizontal: theme.spacing['6'],
  },
  footerSM: {
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderProductConfirmProductLoadedProps = Readonly<{
  baseProduct: Exclude<
    ReturnType<typeof useOrderProductConfirmProductScreenData>['baseProduct'],
    null
  >;
}>;
